import '@mui/material/styles';
import ReactDOM from 'react-dom/client';
import { PureComponent, useEffect, useMemo, memo, Suspense } from 'react';
import { Fade } from "react-awesome-reveal";
import { SnackbarProvider, useSnackbar } from 'notistack';
import { Collapse, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AppQueryProvider } from './Core/Query/QueryProvider';

// Initialize i18n
import './i18n';
import i18n, { t, i18nInitPromise } from './i18n';
import { SocketProvider } from './Core/Socket/SocketProvider';
import './assets/css/components/NotistackCustom.css';
import { IAppState } from './Core/State/IAppState';
import Login from './Components/Login/Login';
import DesktopCashLobby from './Components/Lobby/Cash/DesktopCashLobby';
import MobileCashLobby from './Components/Lobby/Cash/MobileCashLobby';
import DesktopHeader from './Components/Header/DesktopHeader';
import MobileBottomNav from './Components/Header/MobileBottomNav';
import RoomBar from './Components/RoomBar/RoomBar';
import DesktopFooter from './Components/Footer/DesktopFooter';
import { AppStateModel } from './Core/State/AppStateModel';
import { ServerHandler } from './Core/Handler/ServerHandler';
import { InteractionHandler } from './Core/Handler/InteractionHandler';
import { AppSections } from './Core/State/AppSections';
import { IJoinedRoomState } from './Core/State/IJoinedRoomState';
import { IUserState } from './Core/State/IUserState';
import CardImageLoader from './Components/GameRoom/SubComponents/CardImageLoader';
import { preloadAllCardImages } from './Components/GameRoom/SubComponents/Card';
import { initAudioContext, preloadAudios } from './Utils/AudioUtils';
import styles from '/assets/css/components/Lobby.module.css';
import { isMobile } from 'react-device-detect';
import DesktopMttLobby from './Components/Lobby/Tournament/Lobby/DesktopMttLobby';
import MobileMttLobby from './Components/Lobby/Tournament/Lobby/MobileMttLobby';
import DesktopAofLobby from './Components/Lobby/Cash/DesktopAofLobby';
import DesktopCryptoLobby from './Components/Lobby/Cash/DesktopCryptoLobby';
import MobileAofLobby from './Components/Lobby/Cash/MobileAofLobby';
import MobileCryptoLobby from './Components/Lobby/Cash/MobileCryptoLobby';
import { Typography, Button, Box, keyframes } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import GameRoom from './Components/GameRoom/GameRoom';
import DesktopTournamentLobby from './Components/Lobby/Tournament/Lobby/DesktopTournamentLobby';
import DesktopTournamentRoomsLobby from './Components/Lobby/Tournament/RoomsLobby/DesktopTournamentRoomsLobby';
import MobileTournamentRoomsLobby from './Components/Lobby/Tournament/RoomsLobby/MobileTournamentRoomsLobby';
import DesktopTournamentRankingsLobby from './Components/Lobby/Tournament/RankingsLobby/DesktopTournamentRankingsLobby';
import MobileHomeLobby from './Components/Lobby/Home/MobileHomeLobby';
import DesktopHomeLobby from './Components/Lobby/Home/DesktopHomeLobby';
import CasinoLobby from './Components/Lobby/Casino/CasinoLobby';
import CasinoGameView from './Components/Casino/CasinoGameView';
import MaintenancePage from './Components/Maintenance/MaintenancePage';
import { installHsDebugCommands } from './Utils/HsDebug';
import { installDetachedIssueTools } from './Utils/DetachedIssueLogger';
import { isUserSeatedInRoom } from './Utils/RoomSeating';
import { shouldSuppressAutoLogin } from './Utils/AuthAutoLogin';
import successIcon from '/images/notification/success.svg';
import errorIcon from '/images/notification/error.svg';
import infoIcon from '/images/notification/success.svg';
// import WinTournamentModalDebug from './Components/Debug/WinTournamentModalDebug';
// import AddonDebug from './Components/Debug/AddonDebug';
// import TicketsDebug from './Components/Debug/TicketsDebug';

// Bouncing animation for the arrow icon
const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

declare global {
  interface Window {
    renderDetachedRoom: (roomData: IJoinedRoomState, userState: IUserState) => void;
  }
}

const CloseButton = memo(({ snackbarId }: { snackbarId: string | number }) => {
  const { closeSnackbar } = useSnackbar();
  return (
    <IconButton
      size="small"
      aria-label="close"
      color="inherit"
      onClick={() => closeSnackbar(snackbarId)}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );
});

const AppContent = () => {
  useEffect(() => {
    preloadAllCardImages();
    initAudioContext();
    void preloadAudios();
  }, []);

  const App = () => {
    const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === "true";

    if (maintenanceMode) {
      // Stop here and show only the maintenance page
      return <MaintenancePage />;
    }

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const AppComponent = useMemo(() => {
      const FADE_DURATION = 300;
      class PokerApp extends PureComponent<{}, IAppState> {

      public state: IAppState;
      private connection: SocketProvider | null = null;
      private serverHandler: ServerHandler;
      public interactionHandler: InteractionHandler;
      private detachedRooms: { [roomId: string]: boolean } = {};
      private wasDisconnected: boolean = false;
      private lastLobbySection: string | null = null;
      private roomTransfer: { from: string; to: string } | null = null;
      private handleLanguageChange: ((lng: string) => void) | null = null;

      constructor(props: {}) {
        super(props);
        // Initial loading state: check for token/hs token
        const initialParams = new URLSearchParams(window.location.search);
        const initialHsToken = initialParams.get('token');
        const initialStoredToken = localStorage.getItem('sessionToken');
        const suppressInitialAutoLogin = shouldSuppressAutoLogin();

        this.connection = new SocketProvider(import.meta.env.VITE_POKERAPP_SERVER);
        this.state = {
          ...AppStateModel.getInitialState(),
          isLoading: !suppressInitialAutoLogin && Boolean(initialHsToken || initialStoredToken)
        };
        const appStateModel = new AppStateModel(this);
        this.interactionHandler = new InteractionHandler(appStateModel, this.connection, () => {});
        // Pass transferDetachedRoom callback to InteractionHandler
        (this.interactionHandler as any).onTournamentRoomTransfer = this.transferDetachedRoom;
        this.serverHandler = new ServerHandler(this.connection, this.interactionHandler);
        this.connection.on['message'] = this.serverHandler.handleMessage.bind(this.serverHandler);
        try { (window as any).interactionHandler = this.interactionHandler; } catch {}
        try { installHsDebugCommands(this.interactionHandler); } catch {}
        try { installDetachedIssueTools(); } catch {}
      }

      componentDidMount() {
        this.interactionHandler.setEnqueueSnackbar(enqueueSnackbar);
        this.interactionHandler.setCloseSnackbar(closeSnackbar);
        this.interactionHandler.setDetachedRooms(this.detachedRooms);
        
        if (this.connection) {
          this.setState({ connected: false });
          this.connection.on['connected'] = () => {
            if (this.wasDisconnected) {
              this.wasDisconnected = false;
              this.interactionHandler.closeReconnectToastAndShowReconnected();
              this.forceUpdate();
            }
            // Read fresh credentials each time we connect
            if (shouldSuppressAutoLogin()) {
              return;
            }
            const freshParams = new URLSearchParams(window.location.search);
            const freshHsToken = freshParams.get('token');
            const freshStoredToken = localStorage.getItem('sessionToken');
            if (freshHsToken) {
              this.connection?.logIn('', '', freshHsToken);
            } else if (freshStoredToken) {
              this.connection?.logIn('', '');
            }
          };

          // Show toast only when an actual reconnect is scheduled
          this.connection.on['reconnecting'] = () => {
            this.wasDisconnected = true;
            this.interactionHandler.showPersistentReconnectToast();
            this.forceUpdate();
          };

          this.connection.setupWebSocket();
        }

        // Listen for language changes to trigger re-render
        this.handleLanguageChange = (lng: string) => {
          this.setState({ language: lng });
        };
        i18n.on('languageChanged', this.handleLanguageChange);
      }

      componentWillUnmount() {
        // Clean up resources when component unmounts
        if (this.connection) {
          this.connection.clearHeartbeat();
        }
        // Remove language change listener
        if (this.handleLanguageChange) {
          i18n.off('languageChanged', this.handleLanguageChange);
        }
      }

      componentDidUpdate(_: {}, prevState: IAppState) {
        if (prevState?.user?.section !== this.state.user.section) {
          const isNowTable = this.state.user.section === AppSections.POKER_SINGLE_ROOM_VIEW;
          const wasTable = prevState?.user?.section === AppSections.POKER_SINGLE_ROOM_VIEW;
          if (!wasTable && isNowTable) {
            this.lastLobbySection = prevState?.user?.section || null;
          }
        }
      }

      // Transfer detached room state from old to new (for MTT table moves)
      public transferDetachedRoom = (oldRoomId: string, newRoomId: string) => {
        this.roomTransfer = { from: oldRoomId, to: newRoomId };
        this.forceUpdate();
        setTimeout(() => {
          this.roomTransfer = null;
          this.forceUpdate();
        }, 100);
      }

      private handleDetachedRoomsChange = (detachedRooms: { [roomId: string]: boolean }) => {
        this.detachedRooms = detachedRooms;
        // Update the InteractionHandler with the current detached rooms state
        this.interactionHandler.setDetachedRooms(this.detachedRooms);
        // If the current single view room is detached, switch to another room or lobby
        if (this.state.user.singleViewRoomId &&
          detachedRooms[this.state.user.singleViewRoomId] &&
          this.state.user.section === AppSections.POKER_SINGLE_ROOM_VIEW) {
          // Return to the previous lobby/section the user was in before opening the table
          if (this.lastLobbySection) {
            this.interactionHandler.setSection(this.lastLobbySection);
          } else {
            this.interactionHandler.switchToLobby();
          }
        }

        this.forceUpdate();
      }

      private getLoginComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            <Login
              login={this.interactionHandler.login}
              userState={this.state.user}
              loginConflict={this.state.loginConflict}
              onConfirmLoginTakeover={this.interactionHandler.confirmLoginTakeover}
              onCancelLoginTakeover={this.interactionHandler.cancelLoginTakeover}
            />
          </Fade>
        );
      }

      private getHeaderComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            {isMobile ? (
              <MobileBottomNav
                userConnectedCount={this.state.userConnectedCount}
                userState={this.state.user}
                setSection={this.interactionHandler.setSection}
                section={this.state.user.section}
                toggleProfileModal={this.interactionHandler.toggleProfileModal}
                logout={this.interactionHandler.logout}
                login={this.interactionHandler.login}
                uploadAvatar={this.interactionHandler.uploadAvatar}
                onAvatarUpdated={this.interactionHandler.handleAvatarUpdate}
                updateUserSettings={this.interactionHandler.updateUserSettings}
                userSettingsManager={this.interactionHandler.userSettingsManager}
              />
            ) : (
              <DesktopHeader
                userConnectedCount={this.state.userConnectedCount}
                userState={this.state.user}
                setSection={this.interactionHandler.setSection}
                section={this.state.user.section}
                toggleProfileModal={this.interactionHandler.toggleProfileModal}
                logout={this.interactionHandler.logout}
                login={this.interactionHandler.login}
                isReconnecting={this.wasDisconnected}
                uploadAvatar={this.interactionHandler.uploadAvatar}
                onAvatarUpdated={this.interactionHandler.handleAvatarUpdate}
                updateUserSettings={this.interactionHandler.updateUserSettings}
                updateUserSettingsSilent={this.interactionHandler.updateUserSettingsSilent}
                userSettingsManager={this.interactionHandler.userSettingsManager}
              />
            )}
          </Fade>
        );
      }

      private getRoomBarComponent = () => {
        const updateRoomStateWrapper = (data: { roomId: string; state: Partial<any>; }) => {
          this.interactionHandler.updateRoomState(data);
        };

        return (
          <RoomBar
            userState={this.state.user}
            joinedRoomsState={this.state.joinedRooms}
            casinoSessions={this.state.casinoSessions}
            singleViewRoomId={this.state.user.singleViewRoomId}
            singleViewCasinoGameId={this.state.user.singleViewCasinoGameId}
            section={this.state.user.section}
            setSection={this.interactionHandler.setSection}
            sendAction={this.interactionHandler.sendAction}
            leaveRoom={this.interactionHandler.requestLeaveRoom}
            standFromRoom={this.interactionHandler.requestStandFromRoom}
            switchToLobby={this.interactionHandler.switchToLobby}
            requestRoomState={this.interactionHandler.requestRoomState}
            sendBalanceUpdate={this.interactionHandler.sendBalanceUpdate}
            requestSitOptions={this.interactionHandler.requestSitOptions}
            requestSit={this.interactionHandler.requestSit}
            updateRoomState={updateRoomStateWrapper}
            sitOnRoom={this.interactionHandler.requestSitOnRoom}
            requestShowCardsOption={this.interactionHandler.requestShowCardsOption}
            requestBalanceOptions={this.interactionHandler.requestBalanceOptions}
            requestTopUpNextHand={this.interactionHandler.requestTopUpNextHand}
            requestTopUpAlways={this.interactionHandler.requestTopUpAlways}
            requestCancelTopUp={this.interactionHandler.requestCancelTopUp}
            requestSitOutNextHand={this.interactionHandler.requestSitOutNextHand}
            requestCancelSitOutNextHand={this.interactionHandler.requestCancelSitOutNextHand}
            requestSetAutoAction={this.interactionHandler.requestSetAutoAction}
            requestCancelAutoAction={this.interactionHandler.requestCancelAutoAction}
            requestSendTableReaction={this.interactionHandler.requestSendTableReaction}
            requestSitBackIn={this.interactionHandler.requestSitBackIn}
            onDetachedRoomsChange={this.handleDetachedRoomsChange}
            requestTournamentAddonGetOffer={this.interactionHandler.requestTournamentAddonGetOffer}
            roomTransfer={this.roomTransfer}
            closeCasinoGameSession={this.interactionHandler.closeCasinoGameSession}
          />
        );
      }

      private getFooterComponent = () => {
        if (isMobile) {
          return null;
        }
        return (
          <Fade duration={FADE_DURATION}>
            <DesktopFooter
              lobbyState={this.state.lobby}
              lobbyTournamentState={this.state.lobbyTournament}
              userConnectedCount={this.state.userConnectedCount}
            />
          </Fade>
        );
      }

      private getPokerLobbyTexasCashComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            {isMobile ? (
              <MobileCashLobby
                userState={this.state.user}
                lobbyState={this.state.lobby}
                joinedRoomsState={this.state.joinedRooms}
                selectedRoomId={this.state.user.singleViewRoomId}
                joinRoom={this.interactionHandler.joinRoom}
                section={this.state.user.section}
                setSection={this.interactionHandler.setSection}
                logout={this.interactionHandler.logout}
                userConnectedCount={this.state.userConnectedCount}
              />
            ) : (
              <DesktopCashLobby
                userState={this.state.user}
                lobbyState={this.state.lobby}
                selectedRoomId={this.state.user.singleViewRoomId}
                joinRoom={this.interactionHandler.joinRoom}
                joinedRoomsState={this.state.joinedRooms}
                sendChatMessage={this.interactionHandler.sendChatMessage}
              />
            )}
          </Fade>
        );
      }

      private getPokerLobbyAofComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            {isMobile ? (
              <MobileAofLobby
                userState={this.state.user}
                lobbyState={this.state.lobby}
                joinedRoomsState={this.state.joinedRooms}
                selectedRoomId={this.state.user.singleViewRoomId}
                joinRoom={this.interactionHandler.joinRoom}
                section={this.state.user.section}
                setSection={this.interactionHandler.setSection}
                logout={this.interactionHandler.logout}
                userConnectedCount={this.state.userConnectedCount}
              />
            ) : (
              <DesktopAofLobby
                userState={this.state.user}
                lobbyState={this.state.lobby}
                selectedRoomId={this.state.user.singleViewRoomId}
                joinRoom={this.interactionHandler.joinRoom}
                joinedRoomsState={this.state.joinedRooms}
                sendChatMessage={this.interactionHandler.sendChatMessage}
              />
            )}
          </Fade>
        );
      }

      private getPokerLobbyCryptoComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            {isMobile ? (
              <MobileCryptoLobby
                userState={this.state.user}
                lobbyState={this.state.lobby}
                joinedRoomsState={this.state.joinedRooms}
                selectedRoomId={this.state.user.singleViewRoomId}
                joinRoom={this.interactionHandler.joinRoom}
                section={this.state.user.section}
                setSection={this.interactionHandler.setSection}
                logout={this.interactionHandler.logout}
                userConnectedCount={this.state.userConnectedCount}
              />
            ) : (
              <DesktopCryptoLobby
                userState={this.state.user}
                lobbyState={this.state.lobby}
                selectedRoomId={this.state.user.singleViewRoomId}
                joinRoom={this.interactionHandler.joinRoom}
                joinedRoomsState={this.state.joinedRooms}
                sendChatMessage={this.interactionHandler.sendChatMessage}
              />
            )}
          </Fade>
        );
      }

      private getPokerSingleRoomViewComponent = () => {
        const roomId = this.state.user.singleViewRoomId;
        const roomState = roomId ? this.state.joinedRooms.find((room) => room.roomId === roomId) : null;

        const updateRoomStateWrapper = (data: { roomId: string; state: Partial<IJoinedRoomState>; }) => {
          this.interactionHandler.updateRoomState(data);
        };

        if (!roomState) {
          return (
            <Fade duration={FADE_DURATION}>
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: isMobile ? "calc(100dvh - 63px)" : "calc(100dvh - 110px)" }}>
                <Typography variant="h6" style={{ marginBottom: '12px', fontFamily: 'Inter, serif', color: '#FFFFFF' }}>
                  No room selected
                </Typography>
                <Typography variant="body2" style={{ marginBottom: '16px', fontFamily: 'Inter, serif', color: '#8695A6' }}>
                  Returning to lobby...
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => this.interactionHandler.switchToLobby()}
                  style={{ fontWeight: 'bold', backgroundColor: '#F52740', color: 'white' }}
                >
                  {t('actions.returnToLobby')}
                </Button>
              </div>
            </Fade>
          );
        }

        // Don't render the room if it's detached
        if (this.detachedRooms[roomState.roomId]) {
          return (
            <Fade duration={FADE_DURATION}>
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: isMobile ? "calc(100dvh - 63px)" : "calc(100dvh - 110px)" }}>
                <Typography variant="body1" style={{ marginBottom: '16px', fontFamily: 'Inter, serif', position: 'absolute', top: '1rem', right: '5rem' }}>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    {t('table.findOpenTables')}
                    <KeyboardArrowUpIcon 
                      sx={{ 
                        animation: `${bounce} 2s infinite`,
                        fontSize: '1.5rem'
                      }} 
                    />
                  </Box>
                </Typography>
                <Typography variant="h6" style={{ marginBottom: '16px', fontFamily: 'Inter, serif' }}>
                  {t('table.roomDetached')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => this.interactionHandler.switchToLobby()}
                  style={{ fontWeight: 'bold', backgroundColor: '#F52740', color: 'white' }}
                >
                  {t('actions.returnToLobby')}
                </Button>
              </div>
            </Fade>
          );
        }

        const type = roomState.type;
        if (type === 'aof' || type === 'texas') {
          return (
            <Fade duration={FADE_DURATION} style={{ height: isMobile ? "100dvh" : "calc(100dvh - 110px)" }}>
              <GameRoom
                key={roomState.roomId}
                sendAction={this.interactionHandler.sendAction}
                userState={this.state.user}
                roomState={roomState}
                switchToLobby={this.interactionHandler.switchToLobby}
                leaveRoom={this.interactionHandler.requestLeaveRoom}
                standFromRoom={this.interactionHandler.requestStandFromRoom}
                requestRoomState={this.interactionHandler.requestRoomState}
                isUserSeated={isUserSeatedInRoom(roomState, this.state.user.id)}
                isSingleRoomView={true}
                sendBalanceUpdate={this.interactionHandler.sendBalanceUpdate}
                requestSitOptions={this.interactionHandler.requestSitOptions}
                requestSit={this.interactionHandler.requestSit}
                updateRoomState={updateRoomStateWrapper}
                sitOnRoom={this.interactionHandler.requestSitOnRoom}
                requestShowCardsOption={(roomId, cards) =>
                  this.interactionHandler.requestShowCardsOption(roomId, cards)}
                requestBalanceOptions={this.interactionHandler.requestBalanceOptions}
                requestTopUpNextHand={this.interactionHandler.requestTopUpNextHand}
                requestTopUpAlways={this.interactionHandler.requestTopUpAlways}
                requestCancelTopUp={this.interactionHandler.requestCancelTopUp}
                requestSitOutNextHand={this.interactionHandler.requestSitOutNextHand}
                requestCancelSitOutNextHand={this.interactionHandler.requestCancelSitOutNextHand}
                requestSitOutNextBb={this.interactionHandler.requestSitOutNextBb}
                requestCancelSitOutNextBb={this.interactionHandler.requestCancelSitOutNextBb}
                requestSetAutoAction={this.interactionHandler.requestSetAutoAction}
                requestCancelAutoAction={this.interactionHandler.requestCancelAutoAction}
                requestSendTableReaction={this.interactionHandler.requestSendTableReaction}
                requestSitBackIn={this.interactionHandler.requestSitBackIn}
                onShowNotification={(message, variant = 'info') => {
                  enqueueSnackbar(message, { variant });
                }}
              />
            </Fade>
          );
        }
      };

      private getCasinoComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            <CasinoLobby
              userState={this.state.user}
              section={this.state.user.section}
              logout={this.interactionHandler.logout}
              userConnectedCount={this.state.userConnectedCount} />
          </Fade>
        );
      }

      private getCasinoGameViewComponent = () => {
        return null;
      };

      private getBettingComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            <div style={{ height: isMobile ? "calc(100dvh - 63px)" : "calc(100dvh - 200px)", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <h1 style={{ fontFamily: 'Inter, serif' }}>{t('comingSoon.betting')}</h1>
            </div>
          </Fade>
        );
      }

      private getGamesComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            <div style={{ height: isMobile ? "calc(100dvh - 63px)" : "calc(100dvh - 200px)", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <h1 style={{ fontFamily: 'Inter, serif' }}>{t('comingSoon.games')}</h1>
            </div>
          </Fade>
        );
      }

      private getSpinsComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            <div style={{ height: isMobile ? "calc(100dvh - 63px)" : "calc(100dvh - 200px)", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <h1 style={{ fontFamily: 'Inter, serif' }}>{t('comingSoon.spins')}</h1>
            </div>
          </Fade>
        );
      }

      private getErrorComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            <div><h1>Error</h1></div>
          </Fade>
        );
      }

      private getPokerLobbyTexasTournamentsComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            {isMobile ? (
              <MobileMttLobby
                lobbyState={this.state.lobbyTournament}
                userState={this.state.user}
                interactionHandler={this.interactionHandler}
                section={this.state.user.section}
                setSection={this.interactionHandler.setSection}
                logout={this.interactionHandler.logout}
                userConnectedCount={this.state.userConnectedCount}
              />
            ) : (
              <DesktopMttLobby />
            )}
          </Fade>
        );
      }

      private getPokerLobbyTournamentComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            {isMobile ? (
              <MobileMttLobby
                section={this.state.user.section}
                setSection={this.interactionHandler.setSection}
                lobbyState={this.state.lobbyTournament}
                userState={this.state.user}
                interactionHandler={this.interactionHandler}
                logout={this.interactionHandler.logout}
                userConnectedCount={this.state.userConnectedCount}
              />
            ) : (
              <DesktopTournamentLobby
                lobbyState={this.state.lobbyTournament}
                userState={this.state.user}
                interactionHandler={this.interactionHandler}
              />
            )}
          </Fade>
        );
      }

      private getPokerLobbyTournamentRankingsComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            {isMobile ? (
              <DesktopTournamentRankingsLobby
                lobbyState={this.state.lobbyTournamentRankings}
                userState={this.state.user}
              />
            ) : (
              <DesktopTournamentRankingsLobby
                lobbyState={this.state.lobbyTournamentRankings}
                userState={this.state.user}
              />
            )}
          </Fade>
        );
      }

      private getPokerLobbyTournamentRoomsDevComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            {isMobile ? (
              <MobileTournamentRoomsLobby
                userState={this.state.user}
                lobbyState={this.state.lobby}
                joinedRoomsState={this.state.joinedRooms}
                selectedRoomId={this.state.user.singleViewRoomId}
                joinRoom={this.interactionHandler.joinRoom}
                section={this.state.user.section}
                setSection={this.interactionHandler.setSection}
                logout={this.interactionHandler.logout}
                userConnectedCount={this.state.userConnectedCount}
              />
            ) : (
              <DesktopTournamentRoomsLobby
                userState={this.state.user}
                lobbyState={this.state.lobby}
                selectedRoomId={this.state.user.singleViewRoomId}
                joinRoom={this.interactionHandler.joinRoom}
                sendChatMessage={this.interactionHandler.sendChatMessage}
              />
            )}
          </Fade>
        );
      }

      private getHomeLobbyComponent = () => {
        return (
          <Fade duration={FADE_DURATION}>
            {isMobile ? (
              <MobileHomeLobby
                userState={this.state.user}
                lobbyState={this.state.lobby}
                lobbyTournamentState={this.state.lobbyTournament}
                joinedRoomsState={this.state.joinedRooms}
                selectedRoomId={this.state.user.singleViewRoomId}
                joinRoom={this.interactionHandler.joinRoom}
                section={this.state.user.section}
                setSection={this.interactionHandler.setSection}
                logout={this.interactionHandler.logout}
                userConnectedCount={this.state.userConnectedCount}
                interactionHandler={this.interactionHandler}
              />
            ) : (
              <DesktopHomeLobby
                userState={this.state.user}
                lobbyState={this.state.lobby}
                lobbyTournamentState={this.state.lobbyTournament}
                joinedRoomsState={this.state.joinedRooms}
                selectedRoomId={this.state.user.singleViewRoomId}
                joinRoom={this.interactionHandler.joinRoom}
                section={this.state.user.section}
                setSection={this.interactionHandler.setSection}
                logout={this.interactionHandler.logout}
                userConnectedCount={this.state.userConnectedCount}
                interactionHandler={this.interactionHandler}
              />
            )}
          </Fade>
        );
      }

      private getLoadingComponent = () => {
        return (
          <div style={{ backgroundColor: '#191A1F', color: 'white', height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={`${import.meta.env.BASE_URL}images/animated-icon.gif`} alt="PokerSolutions Loading Animation" width={250} height={250} />
          </div>
        );
      }

      appSectionComponents = {
        [AppSections.LOGIN]: this.getLoginComponent,
        [AppSections.LOBBY_TEXAS_CASH]: this.getPokerLobbyTexasCashComponent,
        [AppSections.LOBBY_AOF_FIAT]: this.getPokerLobbyAofComponent,
        [AppSections.LOBBY_AOF_CRYPTO]: this.getPokerLobbyCryptoComponent,
        [AppSections.POKER_SINGLE_ROOM_VIEW]: this.getPokerSingleRoomViewComponent,
        [AppSections.LOBBY_TEXAS_TOURNAMENTS]: this.getPokerLobbyTexasTournamentsComponent,
        [AppSections.CASINO_GAME_VIEW]: this.getCasinoGameViewComponent,
        [AppSections.CASINO]: this.getCasinoComponent,
        [AppSections.BETTING]: this.getBettingComponent,
        [AppSections.GAMES]: this.getGamesComponent,
        [AppSections.SPINS]: this.getSpinsComponent,
        [AppSections.ERROR]: this.getErrorComponent,
        [AppSections.LOBBY_TOURNAMENT]: this.getPokerLobbyTournamentComponent,
        [AppSections.LOBBY_TOURNAMENT_ROOMS]: this.getPokerLobbyTournamentRoomsDevComponent,
        [AppSections.LOBBY_TOURNAMENT_RANKINGS]: this.getPokerLobbyTournamentRankingsComponent,
        [AppSections.LOBBY_HOME]: this.getHomeLobbyComponent,
      };

      renderSection = () => {
        const Component = this.appSectionComponents[this.state.user.section as keyof typeof this.appSectionComponents];
        return Component ? <Component /> : null;
      };

      render() {

        // DEBUG MODE - Uncomment the lines below to show WinTournamentModalDebug instead of login
        // return (
        //   <div style={{ backgroundColor: '#191A1F', color: 'white', height: '100dvh', position: 'relative' }}>
        //     <WinTournamentModalDebug />
        //   </div>
        // );

        // DEBUG MODE - Uncomment the lines below to show AddonDebug instead of login
        // return (
        //   <div style={{ backgroundColor: '#191A1F', color: 'white', height: '100dvh', position: 'relative' }}>
        //     <AddonDebug />
        //   </div>
        // );

        // DEBUG MODE - Uncomment the lines below to show TicketsDebug instead of login
        // return (
        //   <div style={{ backgroundColor: '#191A1F', color: 'white', height: '100dvh', position: 'relative' }}>
        //     <TicketsDebug />
        //   </div>
        // );
        const { user, connected, isLoading } = this.state;

        if (isLoading) {
          return this.getLoadingComponent();
        }

        return (
          <div style={{ backgroundColor: '#121214', color: 'white', height: '100dvh', position: 'relative' }}>
            {user.authenticated === false || connected === false ? (
              <Fade duration={FADE_DURATION}>
                {this.appSectionComponents[AppSections.LOGIN]()}
              </Fade>
            ) : (
              <div className={styles.lobby}>
                {this.getHeaderComponent()}
                {this.getRoomBarComponent()}
                <CardImageLoader />
                {this.renderSection()}
                <CasinoGameView
                  sessions={this.state.casinoSessions}
                  activeSessionId={this.state.user.singleViewCasinoGameId}
                  visible={this.state.user.section === AppSections.CASINO_GAME_VIEW}
                  onBackToCasino={() => this.interactionHandler.setSection(AppSections.CASINO)}
                />
                {this.getFooterComponent()}
              </div>
            )}
            {this.wasDisconnected && (
              <div style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 1300
              }} />
            )}
          </div>
        );
      }
    }

    return PokerApp;
    }, [enqueueSnackbar, closeSnackbar]);

    return <AppComponent />;
  };

  return (
    <SnackbarProvider
      maxSnack={5}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      autoHideDuration={5000}
      preventDuplicate
      dense
      TransitionComponent={Collapse}
      action={(snackbarId) => <CloseButton snackbarId={snackbarId} />}
      iconVariant={{
        success: <img src={successIcon} alt="Success" width={12} height={12} style={{ marginRight: '0.5rem' }} />,
        error: <img src={errorIcon} alt="Error" width={12} height={12} style={{ marginRight: '0.5rem' }} />,
        info: <img src={infoIcon} alt="Info" width={12} height={12} style={{ marginRight: '0.5rem' }} />
      }}
    >
      <App />
    </SnackbarProvider>
  );
};

// Loading fallback - matches body background
const I18nLoadingFallback = () => (
  <div style={{ width: '100vw', height: '100vh', backgroundColor: '#191A1F' }} />
);

// Wait for i18n to be initialized
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
i18nInitPromise.then(() => {
  root.render(
    <AppQueryProvider>
      <Suspense fallback={<I18nLoadingFallback />}>
        <AppContent />
      </Suspense>
    </AppQueryProvider>
  );
});
